import type { Id } from '@repo/go-ui';
import { dismissNotification } from '@repo/go-ui';
import { action, computed, makeObservable, observable, reaction } from 'mobx';

import { NOTIFICATIONS_SERVICE_WEBSOCKET_PATH } from 'src/api/consts';
import {
  getDefaultAlertsPersonalSettings,
  type TAlertRiskLevel,
} from 'src/packages/alerts-preference/AlertsPersonalSettings.api';
import { requireService } from 'src/packages/di';
import { SockClientService } from 'src/packages/sock-client-service/SockClientService';

import { createAlertNotification } from './alert-notifications/notification/createAlertNotification';
import { Flicker } from './Flicker';
import type { IAnomalyNotificationsService } from './IAnomalyNotificationsService';

const ALERT_NOTIFICATIONS_ENABLED_KEY = 'alertNotificationsEnabled';

export type TAlertType = 'MANUAL' | 'ALERT_RULE';

export type TNotification = {
  deviationId: string;
  wellId: number;
  wellName: string;
  wellboreId: number;
  riskLevel: 'NO_RISK' | 'LOW_RISK' | 'MED_RISK' | 'HIGH_RISK';
  bitDepth: number;
  notification: string;
  recommendation: string;
  alertType: TAlertType;
  timestamp: string; // ISO 8601
  createdBy: string;
  showInfoWindow: boolean;
  playSound: boolean;
  showRecommendation: boolean;
  triggerBlink: boolean;
  blinkCount: number;
  infoWindowDurationSeconds: number;
  notificationSize: 'SMALL' | 'LARGE';
};

export class AnomalyNotificationsService implements IAnomalyNotificationsService {
  socket: SockClientService<TNotification>;

  flicker = new Flicker();

  @observable isNotificationsEnabled = true;

  @observable.shallow openNotifications = new Set<Id>();

  constructor(
    private readonly auth = requireService('authService'),
    private readonly alertsPersonalPreference = requireService('alertsPersonalPreference'),
    private readonly soundService = requireService('soundService'),
    private readonly language = requireService('language'),
    private readonly localStorageService = requireService('localStorage'),
  ) {
    this.socket = new SockClientService(NOTIFICATIONS_SERVICE_WEBSOCKET_PATH);
    this.isNotificationsEnabled = this.localStorageService.get<boolean>(ALERT_NOTIFICATIONS_ENABLED_KEY) ?? true;
    makeObservable(this);
  }

  @computed
  get topic() {
    return `/user/${this.auth.userInfo.sub}/queue/notifications/${this.language.language}`;
  }

  @computed get notificationsCount() {
    return this.openNotifications.size;
  }

  passesRiskFilter(notificationRisk: TAlertRiskLevel, minAllowedRisk: TAlertRiskLevel) {
    if (minAllowedRisk === 'NO_RISK') {
      return false;
    }

    const arr = ['LOW_RISK', 'MED_RISK', 'HIGH_RISK'];
    return arr.indexOf(notificationRisk) >= arr.indexOf(minAllowedRisk);
  }

  needSound(notification: TNotification) {
    return this.alertsPersonalPreference.preferences.alertTypes[
      AnomalyNotificationsService.alertTypes[notification.alertType]
    ].soundAlert;
  }

  init() {
    this.socket.init(true);
    this.subscribe(this.topic);

    const topicDisposer = reaction(
      () => this.topic,
      (next, prev) => {
        this.unsubscribe(prev);
        this.subscribe(next);
      },
    );

    return () => {
      this.destroy();
      topicDisposer();
    };
  }

  onNotification = (notification: TNotification) => {
    if (!this.isNotificationsEnabled) {
      return;
    }

    if (
      notification.alertType === 'ALERT_RULE' &&
      !this.alertsPersonalPreference.preferences.alertTypes.alertRules.showInfoWindows
    ) {
      return;
    }

    if (
      notification.alertType === 'MANUAL' &&
      !this.alertsPersonalPreference.preferences.alertTypes.manual.showInfoWindows
    ) {
      return;
    }

    const needFlicker = this.passesRiskFilter(
      notification.riskLevel,
      this.alertsPersonalPreference.preferences.minRiskForScreenFlicker,
    );
    if (needFlicker) {
      this.flicker.add(
        notification.deviationId,
        this.alertsPersonalPreference.preferences.screenFlickerCount ??
          getDefaultAlertsPersonalSettings().screenFlickerCount,
      );
    }
    if (this.needSound(notification)) {
      this.soundService.playSound('alert2');
    }

    const toastId = createAlertNotification({
      notification,
      onClose: () => {
        this.openNotifications.delete(toastId);
        this.flicker.remove(notification.deviationId);

        if (this.needSound(notification)) {
          this.soundService.stopSound('alert2');
        }
      },
    });

    this.openNotifications.add(toastId);
  };

  hideAllNotifications = () => {
    const toastIds = Array.from(this.openNotifications);

    this.openNotifications.clear();

    toastIds.forEach((toastId) => {
      dismissNotification(toastId);
    });
  };

  @action.bound
  toggleNotifications(): void {
    this.isNotificationsEnabled = !this.isNotificationsEnabled;
    this.localStorageService.set(ALERT_NOTIFICATIONS_ENABLED_KEY, this.isNotificationsEnabled);

    if (!this.isNotificationsEnabled) {
      this.hideAllNotifications();
    }
  }

  subscribe(topic: string): void {
    this.socket.subscribe(topic, this.onNotification);
  }

  unsubscribe(topic: string): void {
    this.socket.unsubscribe(topic, this.onNotification);
  }

  static alertTypes = {
    MANUAL: 'manual',
    ALERT_RULE: 'alertRules',
  } as const;

  private destroy(): void {
    this.unsubscribe(this.topic);
    this.socket.destroy();
  }
}
